<!-- components/dialogs/DuckDBQueryDialog.vue -->
<template>
  <v-dialog v-model="dialog" max-width="1200px" scrollable>
    <v-card>
      <v-card-title class="text-h5 d-flex align-center">
        <v-icon class="me-2">mdi-database</v-icon>
        DuckDB Query Tool
        <v-spacer></v-spacer>
        <v-btn 
          color="secondary" 
          variant="outlined" 
          size="small" 
          class="me-2" 
          @click="exportDatabase"
          :loading="isExporting"
        >
          <v-icon class="me-1">mdi-database-export</v-icon>
          Export to Zip
        </v-btn>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';

// Props and emits
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

// Setup local state
const store = useStore();
const dialog = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const isExporting = ref(false);
const errorMessage = ref('');

// Close the dialog
const close = () => {
  dialog.value = false;
};

// Get DuckDB connection from store
const getDBConnection = computed(() => store.getters.getDBConnection);

// Export entire DuckDB database as a .db file
const exportDatabase = async () => {
  try {
    isExporting.value = true;
    errorMessage.value = '';
    
    const conn = getDBConnection.value;
    if (!conn) {
      throw new Error('DuckDB connection not initialized');
    }
    
    // Create a temporary export directory
    const exportDir = `/tmp/export_${Date.now()}`;
    
    // Export the database to the temporary directory
    await conn.query(`EXPORT DATABASE '${exportDir}' (FORMAT PARQUET)`);
    
    // Get the list of exported files
    const db = store.getters.getDuckDBService.db; // Access the DuckDB instance from your store
    if (!db) {
      throw new Error('DuckDB instance not available');
    }
    
    // List the exported files
    const exportedFiles = await db.globFiles(`${exportDir}/*`);
    if (!exportedFiles || exportedFiles.length === 0) {
      throw new Error('No files were exported');
    }
    
    // Create a zip file containing all the exported files
    const JSZip = await import('jszip').then(m => m.default);
    const zip = new JSZip();
    
    // Add each file to the zip
    for (const file of exportedFiles) {
      try {
        // Use the copyFileToBuffer method mentioned in the GitHub issue
        const buffer = await db.copyFileToBuffer(file.fileName);
        if (buffer) {
          // Add the file to the zip
          const filename = file.fileName.split('/').pop();
          zip.file(filename, buffer);
        }
      } catch (err) {
        console.warn(`Could not add file ${file.fileName} to export: ${err.message}`);
      }
    }
    
    // Generate the zip file
    const zipBlob = await zip.generateAsync({type: 'blob'});
    
    // Download the zip file
    const link = document.createElement('a');
    const url = URL.createObjectURL(zipBlob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `duckdb_export_${new Date().toISOString().slice(0,19).replace(/:/g,'-')}.zip`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
  } catch (error) {
    console.error("Database export error:", error);
    errorMessage.value = `Database export error: ${error.message}`;
  } finally {
    isExporting.value = false;
  }
};
</script>